import { defineComponent, h, onMounted, ref, resolveComponent } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { CBadge, CSidebarNav, CNavItem, CNavGroup, CNavTitle } from '@coreui/vue'
import nav from '@/_nav.js'  // Hier wird deine Navigation importiert
import simplebar from 'simplebar-vue'
import 'simplebar-vue/dist/simplebar.min.css'

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }
  if (route.hash === link) {
    return true
  }
  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)
  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }
  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }
  return false
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  setup() {
    const route = useRoute()
    const firstRender = ref(true)

    // Prüfen, ob der Nutzer ein Superuser ist (aus localStorage)
    const isSuperuser = ref(localStorage.getItem('isSuperuser') === 'true')
    const hasTaxReportAccess = ref(localStorage.getItem('taxreport') === 'true') // Steuerreport Berechtigung aus localStorage

    // Menüeinträge basierend auf den Berechtigungen filtern
    const filteredNav = ref([])

    onMounted(() => {
      // Filtere die Navigation, abhängig von Superuser und Steuerreport-Berechtigung
      filteredNav.value = nav.filter(item => {
        // Wenn der Benutzer ein Superuser ist, überspringe die Berechtigungskontrolle
        if (item.requiresSuperuser && !isSuperuser.value) {
          return false
        }

        // Falls es der "Steuerreport" ist, prüfe, ob der Benutzer Zugriff hat
        if (item.name === 'Steuerreport' && !hasTaxReportAccess.value && !isSuperuser.value) {
          return false
        }

        return true
      })

      // Wenn "Steuerreport" nicht angezeigt wird, entferne auch "Steuern" (CNavTitle)
      const hasTaxItems = filteredNav.value.some(item => item.name === 'Steuerreport')
      if (!hasTaxItems) {
        filteredNav.value = filteredNav.value.filter(item => item.name !== 'Steuern')
      }

      firstRender.value = false
    })

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            as: 'div',
            compact: true,
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    as: 'div',
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon
                        ? h(resolveComponent('CIcon'), {
                            customClassName: 'nav-icon',
                            name: item.icon,
                          })
                        : h('span', { class: 'nav-icon' }, h('span', { class: 'nav-icon-bullet' })),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {
              as: 'div',
            },
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {
          as: simplebar,
        },
        {
          default: () => filteredNav.value.map((item) => renderItem(item)),
        },
      )
  },
})

export { AppSidebarNav }
